import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'

import { API_BASE } from '@/api/client'
import { useAuth } from '@/auth/AuthProvider'
import { AppShell } from '@/components/AppShell'
import { Button, Card, EmptyState, ScreenHeader } from '@/components/ui'
import { SignIn } from '@/screens/SignIn'
import { SCREENS } from '@/screens/registry'
import { DEFAULT_PATH, tabForPath, useRouter, type TabDef } from '@/routes'

/**
 * The Admin screen is another agent's file.
 *
 * `src/screens/Admin/**` is not owned by this module, so it is discovered
 * rather than imported: `import.meta.glob` resolves to `{}` when the directory
 * is empty, which keeps `tsc` and `vite build` green before the screen lands
 * and wires it up automatically once it does.
 *
 * **The convention, for whoever builds it:** export a default React component
 * from `src/screens/Admin/index.tsx`. `Admin.tsx`, `AdminScreen.tsx` and
 * `AdminPage.tsx` are also recognised. Anything else is picked up only if it
 * is the single module in the directory.
 *
 * The second pattern excludes `*.test.tsx`. A bare `**\/*.tsx` glob matches the
 * screen's own test files, and a matched module is a real dynamic import: Rollup
 * follows it and code-splits vitest and @testing-library into the production
 * bundle, where they are never executed but are always shipped. It also inflated
 * `adminModuleCount`, so the "no entry point" message counted test files as
 * screens. Negative patterns are resolved by the fast-glob Vite bundles.
 */
type AdminModule = { default?: ComponentType }
const adminModules = import.meta.glob([
  './screens/Admin/**/*.tsx',
  '!./screens/Admin/**/*.test.tsx',
]) as Record<string, () => Promise<AdminModule>>

const ADMIN_ENTRY_NAMES = ['index', 'Admin', 'AdminScreen', 'AdminPage']

function resolveAdminEntry(): (() => Promise<AdminModule>) | null {
  const paths = Object.keys(adminModules)

  for (const name of ADMIN_ENTRY_NAMES) {
    const match = paths.find((path) => path.endsWith(`/screens/Admin/${name}.tsx`))
    if (match) return adminModules[match] ?? null
  }
  // A single module in the directory is unambiguous whatever it is called.
  const only = paths.length === 1 ? paths[0] : undefined
  return only ? (adminModules[only] ?? null) : null
}

const adminModuleCount = Object.keys(adminModules).length
const adminEntry = resolveAdminEntry()

const AdminScreen = adminEntry
  ? lazy(async () => {
      const module = await adminEntry()
      return { default: module.default ?? AdminEntryMissing }
    })
  : null

function AdminEntryMissing() {
  return (
    <EmptyState
      title="Admin screen found, but it exports no default component"
      description="src/screens/Admin/index.tsx must export a default React component for the shell to mount it."
    />
  )
}

export default function App() {
  const router = useRouter()
  const { status, isAdmin, error, refresh } = useAuth()

  if (status === 'loading') return <FullPage>Checking your session…</FullPage>

  if (status === 'error') {
    return (
      <FullPage>
        <Card className="max-w-[460px] gap-3 text-left">
          <h4 className="m-0">Cannot reach the API</h4>
          <p className="m-0 text-body text-muted">
            {error?.message ?? 'The service did not answer.'}
          </p>
          <p className="m-0 text-micro text-muted">
            Trying <code className="font-mono">{API_BASE}</code>. Set{' '}
            <code className="font-mono">VITE_API_BASE</code> if the server is somewhere else.
          </p>
          <div>
            <Button variant="primary" onClick={() => void refresh()}>
              Try again
            </Button>
          </div>
        </Card>
      </FullPage>
    )
  }

  if (status === 'anonymous') return <SignIn />

  const tab = tabForPath(router.path)

  return (
    <AppShell router={router} activeTab={tab?.key ?? null}>
      {!tab ? (
        <UnknownRoute path={router.path} onHome={() => router.navigate(DEFAULT_PATH)} />
      ) : tab.key === 'admin' ? (
        <AdminRoute isAdmin={isAdmin} />
      ) : (
        // The three P2 control screens come from `screens/registry`, which is
        // where the "built but never mounted" failure is guarded — see the
        // comment on `SCREENS`. Admin is the exception below it: that screen
        // belonged to a different agent, so it is discovered by glob rather
        // than imported.
        (SCREENS[tab.key] ?? (() => <PlaceholderScreen tab={tab} />))()
      )}
    </AppShell>
  )
}

/** The Admin tab: role guard first, then whatever the Admin agent installed. */
function AdminRoute({ isAdmin }: { isAdmin: boolean }) {
  if (!isAdmin) {
    return (
      <EmptyState
        title="Admin is for administrators"
        description="Your role does not include administration. Ask an administrator if you need access."
      />
    )
  }

  if (AdminScreen) {
    return (
      <Suspense fallback={<div className="py-16 text-center text-body text-muted">Loading…</div>}>
        <AdminScreen />
      </Suspense>
    )
  }

  if (adminModuleCount === 0) {
    return (
      <EmptyState
        title="Admin screen not installed"
        description="Models & keys and Users & access mount here. The shell loads a default export from src/screens/Admin/index.tsx."
      />
    )
  }

  return (
    <EmptyState
      title="Admin screen has no entry point"
      description={`${adminModuleCount} modules are in src/screens/Admin, but none is named index.tsx, Admin.tsx, AdminScreen.tsx or AdminPage.tsx. Rename one — the shell mounts its default export.`}
    />
  )
}

/**
 * What an unbuilt screen looks like.
 *
 * It says the screen is not built yet and describes what will be there; the
 * build phase that fills it is a planning label, not something to show a
 * person. Nothing on this page is a mock control: an analyst must never be able
 * to click something that looks like it works and does not.
 */
function PlaceholderScreen({ tab }: { tab: TabDef }) {
  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader kicker="Not built yet" title={tab.label} description={tab.blurb} />
      <EmptyState
        title={`${tab.label} is not built yet`}
        description="Nothing on this screen is wired up yet."
      />
    </div>
  )
}

function UnknownRoute({ path, onHome }: { path: string; onHome: () => void }) {
  // Named from the route table so the label cannot drift from `DEFAULT_PATH`.
  const home = tabForPath(DEFAULT_PATH)?.label ?? 'the start'
  return (
    <EmptyState title="No such screen" description={`Nothing is routed at ${path}.`}>
      <Button variant="secondary" onClick={onHome}>
        Back to {home}
      </Button>
    </EmptyState>
  )
}

function FullPage({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-ground px-4 py-16 text-center text-body text-muted">
      {children}
    </div>
  )
}
